/**
 * "Type a letter anywhere, and the symbol search opens with it in the box."
 *
 * This is the same shape of problem `row-cursor.ts` solves for ↑/↓/Space in the
 * screener grid — *does the focused element already mean something by this key?*
 * — and it is one notch more dangerous, because letters are what people type. A
 * misfiring ↓ binding moves a highlight; a misfiring `n` binding eats a
 * character out of the filter someone was halfway through and pops a modal over
 * the screen they were reading. They find out two words later. So this is
 * written as a **whitelist of keystrokes that can mean nothing else**, not a
 * blacklist of places the shortcut must not fire: everything is off by default
 * and each rule below has to argue its way in.
 *
 * Pure, and in `lib/` rather than beside the component, for the reason every
 * module in here is: all of the behaviour is edge cases, and none of them need a
 * rendered dialog to state. The event is described structurally — `key`,
 * `target`, the modifier flags — so a test says a case as an object literal
 * instead of staging a real DOM event.
 *
 * Deliberately *not* an import of `@qufolio/screener-grid`'s `row-cursor.ts`,
 * which has the same `closest()` trick in it. That package is a WebView-hostable
 * island with one barrel export, so importing its predicate would drag AG Grid
 * into the app shell's toolbar; and the two rules are only cousins — the grid
 * lets a `<button>` keep Space, this one takes its letters. Copied reasoning,
 * separate module.
 */

/**
 * The part of a `KeyboardEvent` this reads.
 *
 * `shiftKey` is absent on purpose rather than ignored: see below.
 */
export interface TypeToSearchKeyEvent {
  key: string;
  target: EventTarget | null;
  ctrlKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
  /**
   * True while an IME is mid-composition. The keystroke belongs to the
   * composition, not to us — see `typeToSearchSeed`.
   */
  isComposing?: boolean;
}

/**
 * Tags whose own meaning for a letter outranks ours.
 *
 * `INPUT` covers every one of the app's text surfaces at once — the screener's
 * filter bar, the column picker's search box, the interval picker's count box,
 * the toolbar symbol box, the search dialog's own query field. It is applied to
 * *every* input type, including the checkboxes and radios that would do nothing
 * with a letter, because the two ways of being wrong are not equally bad: a
 * search that failed to open is a click away, and a letter swallowed out of a
 * field someone was typing into is a bug they cannot see the cause of.
 *
 * `SELECT` and `OPTION` are here for a meaning of their own — a native dropdown
 * does first-letter type-ahead, and `s` on an open `<select>` means "jump to the
 * options starting with s".
 *
 * **`BUTTON` is not here, though `row-cursor.ts` lists it**, and the difference
 * is the key rather than the element: Space and Enter *activate* a button, which
 * is why the grid may not take them, but a letter on a focused button means
 * nothing at all in any browser. Excluding buttons would put a dead zone over
 * the toolbar strip, the group chips and the interval buttons — exactly the
 * chrome a user's focus is sitting on when they look up at a chart and start
 * typing a ticker.
 */
const SELF_HANDLING_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT", "OPTION"]);

/**
 * Surfaces that own the keyboard while they are up, whatever is focused inside.
 *
 * A `menu` does first-letter type-ahead by convention, a `listbox` the same, and
 * a modal `dialog` owns every key it does not explicitly hand back — opening a
 * second modal over the top of one is never what a letter meant. This also
 * catches the search dialog itself from a second direction: its panel is
 * `role="dialog"`, so even a keystroke that somehow landed on the panel rather
 * than in its query field cannot re-enter through this path.
 */
const KEYBOARD_OWNING_ROLES = '[role="menu"],[role="listbox"],[role="dialog"]';

/** Does the focused element already mean something by a plain letter? */
export function handlesOwnLetters(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (SELF_HANDLING_TAGS.has(target.tagName)) return true;
  // `closest`, not the element itself: a rich text surface puts focus on a child
  // of the editable host, and the DSL expression editor is one. `isContentEditable`
  // would say this in one word but jsdom does not implement it, which would make
  // the case untestable — the same call `row-cursor.ts` makes.
  if (target.closest('[contenteditable]:not([contenteditable="false"])') != null) return true;
  return target.closest(KEYBOARD_OWNING_ROLES) != null;
}

/**
 * ASCII letters only, and only one of them.
 *
 * The length check is what excludes the whole named-key space in one rule —
 * `ArrowDown`, `Enter`, `Escape`, `F3`, `Dead`, `Process` are all longer than a
 * character, so no key name has to be listed here to be kept out.
 *
 * Digits and punctuation are excluded because they are what a *shortcut* is made
 * of and because no ticker begins with one; `/` in particular is a search key in
 * two browsers and several apps, and would be the obvious thing to bind next if
 * anyone wanted it. `é` and `ß` are excluded with them: this universe is
 * US-listed and ASCII, so an accented first keystroke is far likelier to be the
 * start of something else than the start of a company name we could match.
 */
const LETTER = /^[a-z]$/i;

/**
 * The letter to seed the search with, or `null` for "not ours".
 *
 * The rules, in the order they eliminate:
 *
 * - **Ctrl / Cmd / Alt held is a command, never a letter.** `Cmd+R`, `Ctrl+F`,
 *   `Alt+D` belong to the browser and the OS, and an app that swallows them is
 *   broken in a way users blame the app for. **Plain Shift is fine** and is not
 *   even read: a capital letter is still a letter, and `S` is a perfectly normal
 *   way to start typing a ticker. (`row-cursor.ts` *does* reject Shift, because
 *   Shift+↓ extends a selection. Shift+`n` means `N`.)
 * - **A composing keystroke belongs to the IME.** Mid-composition a keydown
 *   reports `isComposing`, or `key === "Process"` on the browsers that predate
 *   it. Taking that key hijacks the first stroke of a Japanese or Chinese input
 *   sequence — and the *letters* are exactly the strokes an IME is built out of,
 *   so this is the common case there rather than an exotic one.
 * - **One ASCII letter**, per `LETTER` above.
 * - **Nothing that already means something by it**, per `handlesOwnLetters`.
 *
 * The letter comes back with its case intact, because it is about to be shown in
 * a text field the user is still typing into — matching is case-insensitive, but
 * seeing `N` when you pressed Shift+N is not a matching question.
 */
export function typeToSearchSeed(event: TypeToSearchKeyEvent): string | null {
  if (event.ctrlKey || event.metaKey || event.altKey) return null;
  if (event.isComposing || event.key === "Process") return null;
  if (!LETTER.test(event.key)) return null;
  if (handlesOwnLetters(event.target)) return null;
  return event.key;
}
